import React, { useState, useRef, useEffect } from 'react'
import { css } from '@emotion/css'

import IconClock from '../icon/IconClock'
import {
  Box,
  InputGroup,
  InputRightElement,
  ChakraProps,
  AutocompleteDropdown
} from 'front-end/src/components'

import { useClickAway } from '../../utils/useClickAway'

import IconCross from '../icon/IconCross'
import Input from '../form/input/Input'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'

const options = [...Array(24).keys()].reduce(
  (a: string[], i) =>
    a.concat(`${i < 10 ? `0${i}` : i}:00`, `${i < 10 ? `0${i}` : i}:30`),
  []
)

interface TimePickerProps extends ChakraProps {
  value?: string
  placeholder?: string
  onChangeHandler?: (value: string) => void
  isDisabled?: boolean
  isClearable?: boolean
  dataTest?: string
}

const TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/gi

const getFormattedTime = (time: string) => {
  let [hour, minutes] = time.split(':')

  if (Number(hour) < 10 && !hour.includes('0')) {
    hour = `0${hour}`
  }

  return `${hour}:${minutes}`
}

const TimePicker = ({
  value,
  onChangeHandler,
  placeholder,
  isDisabled = false,
  isClearable = false,
  dataTest = 'TimePicker',
  ...props
}: TimePickerProps) => {
  const [isOpened, setOpened] = useState(false)
  const [isFocused, setFocused] = useState(false)
  const [localValue, setLocalValue] = useState(value || '')
  const wrapperRef = useRef(null)
  const isUpdated = value !== localValue
  const { theme } = useLooopTheme()

  const closeAutoComplete = () => {
    if (!isFocused) {
      setOpened(false)
    }
  }

  const handleChange = (inputValue: string) => {
    setLocalValue(inputValue)
  }

  const handleFocus = () => {
    setOpened(true)
    setFocused(true)
  }

  const clearValue = () => {
    if (onChangeHandler) {
      onChangeHandler('')
    } else {
      setLocalValue('')
    }
  }

  useClickAway(isOpened, wrapperRef, closeAutoComplete)

  useEffect(() => {
    if (isOpened || !isUpdated) return

    const isValid = !!localValue && localValue.match(TIME_REGEX) !== null

    if (localValue && isValid) {
      const formattedValue = getFormattedTime(localValue)

      if (onChangeHandler) onChangeHandler(formattedValue)
      if (formattedValue !== localValue) setLocalValue(formattedValue)
    } else {
      setLocalValue(value || '')
    }
  }, [localValue, isOpened])

  useEffect(() => {
    if (isUpdated) {
      setLocalValue(value || '')
    }
  }, [value])

  return (
    <InputGroup
      w="120px"
      ml="8px"
      {...props}
      ref={wrapperRef}
      data-test={dataTest}
    >
      <Input
        type="text"
        fullWidth
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={isDisabled}
        className={css`
          padding-right: ${isClearable ? 60 : 40}px;
          text-align: left !important;
        `}
      />
      <AutocompleteDropdown
        options={options}
        isOpened={isOpened}
        setOpen={setOpened}
        value={localValue}
        setValue={setLocalValue}
      />
      <InputRightElement pointerEvents="none" w="auto">
        {!!localValue && isClearable && (
          <Box
            pointerEvents="auto"
            cursor="pointer"
            onClick={clearValue}
            as="span"
            d="block"
            fontSize={0}
            mr="6px"
          >
            <IconCross w="20px" h="20px" />
          </Box>
        )}
        <Box
          w="32px"
          pr="4px"
          d="flex"
          alignItems="center"
          justifyContent="center"
        >
          <IconClock color={theme.colors.grey[500]} />
        </Box>
      </InputRightElement>
    </InputGroup>
  )
}

export default TimePicker
